import { useCallback, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import SimplePeer from "simple-peer";
import JSZip from "jszip";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { UserAvatar } from "@/components/UserAvatar";
import { brutalfilesGetPublicShare } from "@/lib/api";
import { Download, FileText, Loader2 } from "lucide-react";

const CHUNK_SIZE = 16 * 1024;

function formatBytes(n) {
  if (n == null || n === 0) return "0 B";
  const k = 1024;
  const sizes = ["B", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(n) / Math.log(k));
  return `${parseFloat((n / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
}

function getPublicWsUrl(shareId) {
  const protocol = window.location.protocol === "https:" ? "wss:" : "ws:";
  const host = window.location.host;
  return `${protocol}//${host}/api/ws/brutalfiles?public=1&share_id=${encodeURIComponent(shareId)}`;
}

export default function BrutalFilesPublicSharePage() {
  const { shareId } = useParams();
  const [shareData, setShareData] = useState(null);
  const [loadingMeta, setLoadingMeta] = useState(true);
  const [statusText, setStatusText] = useState("");
  const [errorText, setErrorText] = useState("");
  const [running, setRunning] = useState(false);
  const [receivedBytes, setReceivedBytes] = useState(0);
  const wsRef = useRef(null);
  const presenceWsRef = useRef(null);
  const presenceReconnectRef = useRef(null);
  const shouldReconnectPresenceRef = useRef(true);
  const peerRef = useRef(null);
  const zipRef = useRef(null);
  const currentFileRef = useRef(null);
  const currentChunksRef = useRef([]);
  const transferActiveRef = useRef(false);
  const transferCompletedRef = useRef(false);
  const ownerName = shareData?.owner?.nombre || shareData?.owner?.email || "usuario";
  const ownerOnline = !!shareData?.owner?.online;

  useEffect(() => {
    let mounted = true;
    setLoadingMeta(true);
    brutalfilesGetPublicShare(shareId)
      .then((data) => {
        if (!mounted) return;
        setShareData(data);
        setErrorText("");
      })
      .catch((err) => {
        if (!mounted) return;
        setErrorText(err?.response?.data?.detail || "Enlace no disponible");
      })
      .finally(() => {
        if (mounted) setLoadingMeta(false);
      });
    return () => {
      mounted = false;
      transferActiveRef.current = false;
      shouldReconnectPresenceRef.current = false;
      if (presenceReconnectRef.current) {
        clearTimeout(presenceReconnectRef.current);
        presenceReconnectRef.current = null;
      }
      try { presenceWsRef.current?.close(); } catch (_) {}
      try { wsRef.current?.close(); } catch (_) {}
      try { peerRef.current?.destroy(); } catch (_) {}
    };
  }, [shareId]);

  useEffect(() => {
    if (!shareId) return undefined;
    shouldReconnectPresenceRef.current = true;

    const requestOwnerStatus = () => {
      if (presenceWsRef.current?.readyState !== WebSocket.OPEN) return;
      try {
        presenceWsRef.current.send(JSON.stringify({ type: "public_owner_status" }));
      } catch (_) {}
    };

    const connectPresence = () => {
      const ws = new WebSocket(getPublicWsUrl(shareId));
      presenceWsRef.current = ws;

      ws.onopen = () => {
        requestOwnerStatus();
      };
      ws.onmessage = (event) => {
        try {
          const msg = JSON.parse(event.data);
          const type = msg.type || msg.event;
          if (type === "public_owner_status") {
            const online = !!msg.online;
            setShareData((prev) => {
              if (!prev) return prev;
              return {
                ...prev,
                owner: {
                  ...(prev.owner || {}),
                  online,
                },
              };
            });
          }
        } catch (_) {}
      };
      ws.onclose = () => {
        if (shouldReconnectPresenceRef.current) {
          presenceReconnectRef.current = setTimeout(connectPresence, 1500);
        }
      };
    };

    const onVisible = () => {
      if (document.visibilityState === "visible") requestOwnerStatus();
    };
    const onFocus = () => {
      requestOwnerStatus();
    };

    connectPresence();
    if (typeof document !== "undefined") {
      document.addEventListener("visibilitychange", onVisible);
    }
    if (typeof window !== "undefined") {
      window.addEventListener("focus", onFocus);
    }
    return () => {
      shouldReconnectPresenceRef.current = false;
      if (presenceReconnectRef.current) {
        clearTimeout(presenceReconnectRef.current);
        presenceReconnectRef.current = null;
      }
      if (typeof document !== "undefined") {
        document.removeEventListener("visibilitychange", onVisible);
      }
      if (typeof window !== "undefined") {
        window.removeEventListener("focus", onFocus);
      }
      try { presenceWsRef.current?.close(); } catch (_) {}
    };
  }, [shareId]);

  const startDownload = useCallback(() => {
    if (!shareData || !shareId || running) return;
    setErrorText("");
    setStatusText("Conectando P2P...");
    setRunning(true);
    transferActiveRef.current = true;
    transferCompletedRef.current = false;
    setReceivedBytes(0);
    zipRef.current = new JSZip();
    currentFileRef.current = null;
    currentChunksRef.current = [];

    const ws = new WebSocket(getPublicWsUrl(shareId));
    wsRef.current = ws;

    const fail = (msg) => {
      setErrorText(msg);
      setStatusText("");
      setRunning(false);
      transferActiveRef.current = false;
      try { ws.close(); } catch (_) {}
      try { peerRef.current?.destroy(); } catch (_) {}
      peerRef.current = null;
    };

    ws.onmessage = (event) => {
      try {
        const msg = JSON.parse(event.data);
        const type = msg.type || msg.event;
        if (type === "public_share_unavailable") {
          fail("El emisor no está disponible ahora mismo.");
          return;
        }
        if (type === "public_receiver_ready") {
          const receiverId = msg.receiver_id;
          const peer = new SimplePeer({ initiator: true, trickle: true });
          peerRef.current = peer;

          peer.on("signal", (data) => {
            if (ws.readyState === WebSocket.OPEN) {
              ws.send(JSON.stringify({ type: "public_signal", share_id: shareId, receiver_id: receiverId, data }));
            }
          });

          peer.on("connect", () => {
            setStatusText("Recibiendo archivos...");
          });

          peer.on("data", async (rawData) => {
            try {
              const str = typeof rawData === "string" ? rawData : new TextDecoder().decode(rawData);
              const obj = JSON.parse(str);
              if (obj.type === "share_batch_start") {
                setStatusText("Recibiendo archivos...");
                return;
              }
              if (obj.type === "file_start") {
                currentFileRef.current = {
                  name: obj.name || "archivo",
                  size: obj.size || 0,
                };
                currentChunksRef.current = [];
                return;
              }
              if (obj.type === "file_end") {
                const meta = currentFileRef.current;
                if (meta) {
                  const blob = new Blob(currentChunksRef.current);
                  zipRef.current.file(meta.name, blob);
                }
                currentFileRef.current = null;
                currentChunksRef.current = [];
                return;
              }
              if (obj.type === "share_batch_end") {
                transferActiveRef.current = false;
                transferCompletedRef.current = true;
                setStatusText("Generando ZIP...");
                const zipBlob = await zipRef.current.generateAsync({ type: "blob", compression: "DEFLATE" });
                const a = document.createElement("a");
                a.href = URL.createObjectURL(zipBlob);
                a.download = `${(shareData.title || "archivos").replace(/[^\w\- ]/g, "").trim() || "archivos"}.zip`;
                a.click();
                URL.revokeObjectURL(a.href);
                try {
                  peer.send(JSON.stringify({ type: "share_ack", share_id: shareId }));
                } catch (_) {}
                setStatusText("Descarga completada.");
                setRunning(false);
                return;
              }
            } catch (_) {
              const len = rawData?.byteLength ?? rawData?.length ?? 0;
              if (currentFileRef.current) {
                currentChunksRef.current.push(rawData);
                setReceivedBytes((prev) => prev + len);
              }
            }
          });

          peer.on("error", () => {
            if (transferCompletedRef.current) return;
            fail("Se perdió la conexión P2P durante la transferencia.");
          });
          peer.on("close", () => {
            if (transferActiveRef.current) fail("Conexión cerrada antes de completar la descarga.");
          });
          return;
        }
        if (type === "public_signal") {
          if (peerRef.current) peerRef.current.signal(msg.data);
          return;
        }
      } catch (_) {}
    };

    ws.onerror = () => fail("No se pudo conectar al canal de señalización.");
    ws.onclose = () => {
      if (transferActiveRef.current) fail("Canal cerrado antes de completar la descarga.");
    };
  }, [shareData, shareId, running]);

  if (loadingMeta) {
    return (
      <div className="min-h-dvh bg-white flex items-center justify-center px-4">
        <Loader2 className="w-6 h-6 animate-spin text-neutral-500" />
      </div>
    );
  }

  if (errorText && !shareData) {
    return (
      <div className="min-h-dvh bg-white flex items-center justify-center px-4">
        <Card className="w-full max-w-2xl">
          <CardContent className="py-10 text-center">
            <p className="text-lg font-medium">No se puede abrir este enlace</p>
            <p className="text-sm text-muted-foreground mt-2">{errorText}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-dvh bg-white flex items-center justify-center p-4">
      <Card className="w-full max-w-2xl border-neutral-200 shadow-sm">
        <CardContent className="p-6 space-y-4">
          <div className="space-y-2">
            <h1 className="text-2xl font-semibold text-neutral-900">{shareData?.title || "Archivos compartidos"}</h1>
            <p className="text-sm text-neutral-600">{shareData?.description || "Se han compartido archivos contigo."}</p>
          </div>

          <div className="rounded-xl border border-neutral-200 p-3">
            <div className="relative flex flex-col items-center text-center">
              <span
                className={`absolute top-1 right-1 w-3 h-3 rounded-full shrink-0 ${shareData?.owner?.online ? "bg-green-500" : "bg-red-500"}`}
                aria-label={shareData?.owner?.online ? "Conectado" : "Desconectado"}
              />
              <UserAvatar user={shareData?.owner || {}} size={56} className="mb-2" />
              <p className="font-semibold text-neutral-900 truncate w-full px-1">{shareData?.owner?.nombre || shareData?.owner?.email || "Usuario"}</p>
              <span className="text-xs text-neutral-500 bg-neutral-100 rounded-full px-2.5 py-0.5 mt-1 truncate max-w-full">
                @{shareData?.owner?.email?.split("@")[0] || "usuario"}
              </span>
              <p className="text-xs text-neutral-600 mt-2">{shareData?.owner?.online ? "Conectado" : "Desconectado"}</p>
            </div>
          </div>

          <div className="space-y-2">
            {(shareData?.files_meta || []).map((file, index) => (
              <div key={`${file.name}-${index}`} className="flex items-center gap-3 rounded-md border border-neutral-200 px-3 py-2">
                <FileText className="w-4 h-4 text-neutral-500 shrink-0" strokeWidth={1.5} />
                <span className="text-sm text-neutral-800 truncate flex-1">{file.name}</span>
                <span className="text-xs text-neutral-500 shrink-0">{formatBytes(file.size)}</span>
              </div>
            ))}
          </div>

          <div className="flex items-center justify-between">
            <Badge variant="outline" className="text-xs font-normal">
              Peso total: {formatBytes(shareData?.total_size || 0)}
            </Badge>
            {receivedBytes > 0 ? (
              <span className="text-xs text-neutral-500">Recibido: {formatBytes(receivedBytes)}</span>
            ) : null}
          </div>

          {!ownerOnline ? (
            <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
              Avisa al usuario {ownerName} que se conecte a la pestaña de "Archivos" para descargar.
            </p>
          ) : null}

          {statusText ? <p className="text-xs text-neutral-600">{statusText}</p> : null}
          {errorText ? <p className="text-xs text-red-600">{errorText}</p> : null}

          <Button type="button" onClick={startDownload} disabled={running || !ownerOnline} className="w-full gap-2">
            {running ? <Loader2 className="w-4 h-4 animate-spin" strokeWidth={1.5} /> : <Download className="w-4 h-4" strokeWidth={1.5} />}
            {running ? "Procesando descarga..." : "Descargar ZIP"}
          </Button>
          <p className="text-[11px] text-neutral-500 text-center">
            La descarga funciona en modo P2P: el emisor debe mantener su pestaña abierta.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
